import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, ArrowRight, Filter, ChevronDown, Layers, Package, Wrench } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { getCaseStudies, getCaseStudyCategories } from '../services/api';
import { motion } from 'framer-motion';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const CaseStudies = () => {
  const [studies, setStudies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await getCaseStudyCategories();
        setCategories(res.data || []);
      } catch {}
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchStudies = async () => {
      setLoading(true);
      try {
        const res = await getCaseStudies(selectedCategory || undefined);
        setStudies(res.data || []);
      } catch {}
      finally { setLoading(false); }
    };
    fetchStudies();
  }, [selectedCategory]);

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${API_BASE}${url}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f8f9fb] to-white" data-testid="case-studies-page">
      {/* Header */}
      <section className="bg-[#2E3A6F] text-white py-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-64 h-64 rounded-full bg-white/20 blur-3xl" />
          <div className="absolute bottom-0 left-10 w-48 h-48 rounded-full bg-white/10 blur-2xl" />
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4 relative z-10">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl font-bold"
          >
            Case Studies
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-lg text-white/80 max-w-2xl mx-auto"
          >
            Real-world results from our precision tooling solutions
          </motion.p>
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          {/* Category Filter */}
          {categories.length > 0 && (
            <div className="mb-8" data-testid="case-study-filters">
              <div className="flex items-center gap-3 flex-wrap">
                <Button
                  variant={selectedCategory === '' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory('')}
                  className={selectedCategory === '' ? 'bg-[#2E3A6F]' : ''}
                  data-testid="filter-all"
                >
                  <Layers size={14} className="mr-1.5" /> All Projects
                </Button>
                {categories.map(cat => (
                  <Button
                    key={cat}
                    variant={selectedCategory === cat ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedCategory(cat)}
                    className={selectedCategory === cat ? 'bg-[#2E3A6F]' : ''}
                    data-testid={`filter-${cat}`}
                  >
                    {cat}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Studies Grid */}
          {loading ? (
            <div className="grid md:grid-cols-2 gap-6">
              {[...Array(4)].map((_, i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-0">
                    <div className="h-48 bg-gray-200" />
                    <div className="p-6 space-y-3">
                      <div className="h-5 bg-gray-200 rounded w-3/4" />
                      <div className="h-4 bg-gray-100 rounded w-1/2" />
                      <div className="h-16 bg-gray-100 rounded" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : studies.length === 0 ? (
            <Card className="border-0 shadow-lg">
              <CardContent className="p-12 text-center space-y-4">
                <FileText className="mx-auto text-gray-300" size={48} />
                <h3 className="text-xl font-bold text-gray-900">No Case Studies Yet</h3>
                <p className="text-gray-600">Check back soon for detailed project reports and trial results.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {studies.map((study, index) => (
                <motion.div
                  key={study.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.08 }}
                >
                  <Link to={`/case-studies/${study.id}`} data-testid={`case-study-card-${study.id}`}>
                    <Card className="border-0 hover:shadow-xl transition-all duration-300 overflow-hidden group h-full">
                      {/* Cover Image */}
                      <div className="relative h-48 bg-gradient-to-br from-[#2E3A6F] to-[#1a1f3a] overflow-hidden">
                        {study.cover_image ? (
                          <img
                            src={getImageUrl(study.cover_image)}
                            alt={study.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <FileText size={48} className="text-white/30" />
                          </div>
                        )}
                        {study.category && (
                          <span className="absolute top-3 left-3 bg-white/90 text-[#2E3A6F] text-xs font-semibold px-3 py-1 rounded-full backdrop-blur-sm">
                            {study.category}
                          </span>
                        )}
                        {study.trial_reports?.length > 0 && (
                          <span className="absolute top-3 right-3 bg-[#2E3A6F]/90 text-white text-xs font-medium px-2.5 py-1 rounded-full backdrop-blur-sm flex items-center gap-1">
                            <FileText size={12} /> {study.trial_reports.length} Report{study.trial_reports.length > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>

                      <CardContent className="p-6 space-y-3">
                        <h3 className="text-lg font-bold text-gray-900 group-hover:text-[#2E3A6F] transition-colors line-clamp-2">
                          {study.title}
                        </h3>
                        <p className="text-sm text-[#2E3A6F] font-medium">{study.project_name}</p>
                        <p className="text-sm text-gray-600 line-clamp-2">{study.description}</p>

                        {/* Products used */}
                        {study.products?.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 pt-1">
                            {study.products.slice(0, 3).map((p, i) => (
                              <span key={i} className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full flex items-center gap-1">
                                <Package size={10} /> {p.product_name}
                              </span>
                            ))}
                            {study.products.length > 3 && (
                              <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                                +{study.products.length - 3} more
                              </span>
                            )}
                          </div>
                        )}

                        {/* Highlights preview */}
                        {study.highlights?.length > 0 && (
                          <div className="pt-2 border-t">
                            <p className="text-xs text-gray-500 font-medium mb-1.5">Key Highlights</p>
                            <ul className="space-y-1">
                              {study.highlights.slice(0, 2).map((h, i) => (
                                <li key={i} className="text-xs text-gray-600 flex items-start gap-1.5">
                                  <span className="text-green-500 mt-0.5 flex-shrink-0">&#10003;</span> {h}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        <div className="flex items-center text-sm text-[#2E3A6F] font-medium pt-2 group-hover:translate-x-1 transition-transform">
                          View Full Case Study <ArrowRight size={14} className="ml-1" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default CaseStudies;
